// CompareRequirements — the requirements DELTA between two versions, shown in
// the terms a person asks the question in (Alex, 2026-09-04): "what changed in
// the requirements between the sources WITHOUT the change request and WITH
// it?" Two modes over one backend route (GET /api/docs/features/diff):
//
//   by sources version   — pick two SOURCES versions; each resolves to the
//                          latest requirements version derived from it. The
//                          header names the documents that differ between the
//                          two (the change request), so the cause travels with
//                          the effect. This is the default when the project
//                          has requirements for more than one sources version.
//   by requirements version — the original "what changed in vN" picker.
//
// Every row is expandable: kind, origin (which material, the quote), what
// implements it, and for a CHANGED row the fields that moved with the
// description as it WAS beside the description as it IS. Deep-linkable: the
// parent owns { by, from, to } (URL params) and is told of every change.
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, FileText, LayoutGrid, ClipboardList, X } from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";

const KIND_CHIP = {
  functional: null, quality: "quality", constraint: "constraint", design: "design",
  non_goal: "out of scope", noise: "not a requirement",
};
const FIELD_LABEL = {
  name: "name", kind: "kind", description: "description", use_cases: "use cases", origin: "origin",
  material: "source material", app: "implementing app", built_ref: "implementing object", quote: "origin quote",
};

// `featuresVersions`: [{ version, materials_version }] newest first (the tab's list).
// `compare`: { by: 'materials'|'features', from, to } — `to` required, `from` may be null (= previous).
export default function CompareRequirements({ featuresVersions = [], compare, onChange, onClose, onOpenFeature, onOpenMaterial }) {
  const [diff, setDiff] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const by = compare?.by === "features" ? "features" : "materials";

  // The sources versions that HAVE requirements (distinct materials_version of
  // the requirements versions), newest first — the only ones comparable.
  const sourcesVersions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of featuresVersions) {
      const mv = v.materials_version;
      if (mv == null || seen.has(mv)) continue;
      seen.add(mv);
      out.push({ materials_version: mv, features_version: v.version });
    }
    return out.sort((a, b) => b.materials_version - a.materials_version);
  }, [featuresVersions]);

  useEffect(() => {
    if (!compare?.to) return undefined;
    let alive = true;
    setDiff(null); setErr("");
    const q = by === "materials"
      ? `materials_to=${compare.to}${compare.from != null ? `&materials_from=${compare.from}` : ""}`
      : `to=${compare.to}${compare.from != null ? `&from=${compare.from}` : ""}`;
    fetchData(`docs/features/diff?${q}`, { method: "GET" })
      .then((r) => { if (alive) setDiff(r); })
      .catch((e) => { if (alive) setErr(String(e?.message || "Could not read the comparison.")); });
    return () => { alive = false; };
  }, [by, compare?.from, compare?.to]);

  if (!compare?.to) return null;
  const toggle = (k) => setOpen((o) => { const n = new Set(o); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // The pickers list the comparable versions — and always the two being
  // compared, so a deep link renders its own values while the tab's version
  // list is still loading (the diff answers before the vault does).
  const versionsFor = useMemo(() => {
    const base = by === "materials" ? sourcesVersions.map((s) => s.materials_version) : featuresVersions.map((v) => v.version);
    const set = new Set(base);
    for (const n of [compare?.to, compare?.from]) if (Number.isInteger(n) && n > 0) set.add(n);
    return [...set].sort((a, b) => b - a);
  }, [by, sourcesVersions, featuresVersions, compare?.to, compare?.from]);
  const label = (n) => (by === "materials" ? `sources v${n}` : `requirements v${n}`);
  const prevOf = (to) => versionsFor.filter((n) => n < to).sort((a, b) => b - a)[0] ?? null;
  const mats = diff?.materials;
  const counts = diff?.counts || { added: diff?.added?.length || 0, changed: diff?.changed?.length || 0, removed: diff?.removed?.length || 0 };

  return (
    <div className="rounded-lg border border-page-divider p-3 space-y-2" data-testid="features-compare" data-compare-by={by}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-page-text">Compare requirements</span>
        <span className="inline-flex rounded-md border border-page-divider overflow-hidden" role="tablist" aria-label="Compare by">
          {[["materials", "by sources version"], ["features", "by requirements version"]].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={by === k} data-testid={`features-compare-by-${k}`}
              onClick={() => {
                if (by === k) return;
                // Re-anchor on the newest version of the other axis.
                const to = k === "materials" ? sourcesVersions[0]?.materials_version : featuresVersions[0]?.version;
                onChange?.({ by: k, to: to ?? null, from: null });
              }}
              className={`px-2 py-1 bg-transparent border-0 cursor-pointer ${by === k ? "bg-card-border/40 text-page-text font-medium" : "text-page-muted hover:text-page-text"}`}>
              {l}
            </button>
          ))}
        </span>
        <span className="text-page-muted">from</span>
        <select data-testid="features-compare-from" value={compare.from ?? ""} onChange={(e) => onChange?.({ by, to: compare.to, from: e.target.value === "" ? null : Number(e.target.value) })}
          className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
          <option value="">{prevOf(compare.to) != null ? `${label(prevOf(compare.to))} (previous)` : "nothing (first version)"}</option>
          {versionsFor.filter((n) => n !== compare.to).map((n) => <option key={n} value={n}>{label(n)}</option>)}
        </select>
        <span className="text-page-muted">to</span>
        <select data-testid="features-compare-to" value={compare.to} onChange={(e) => onChange?.({ by, to: Number(e.target.value), from: compare.from != null && compare.from !== Number(e.target.value) ? compare.from : null })}
          className="text-xs px-1.5 py-1 rounded-md border border-page-divider bg-card-bg text-page-text cursor-pointer">
          {versionsFor.map((n) => <option key={n} value={n}>{label(n)}</option>)}
        </select>
        {diff ? (
          <span className="text-page-muted" data-testid="features-compare-counts">
            {counts.added} added · {counts.changed} changed · {counts.removed} removed
            {diff.from_version && diff.to_version ? <> — requirements v{diff.from_version.version} → v{diff.to_version.version}</> : diff.to_version ? <> — requirements v{diff.to_version.version}</> : null}
          </span>
        ) : err ? <span className="text-red-600" data-testid="features-compare-error">{err}</span> : <Loader2 size={12} className="animate-spin" />}
        {onClose ? (
          <button type="button" onClick={onClose} data-testid="features-compare-close" aria-label="Close the comparison"
            className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5"><X size={13} /></button>
        ) : null}
      </div>

      {/* THE CAUSE: which sources differ between the two versions compared. */}
      {diff && mats ? (
        <div className="text-xs rounded-md px-2 py-1.5 space-y-0.5" style={{ background: "var(--table-stripe)" }} data-testid="features-compare-sources">
          <div className="text-page-muted">
            Sources {mats.from != null ? <>v{mats.from} → v{mats.to}</> : <>v{mats.to} (the first analysed version)</>}
            {mats.from != null ? <> — {mats.summary}</> : null}
          </div>
          {mats.added.map((m) => (
            <div key={`ma${m.id}`} className="flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300">
              <FileText size={11} /> <span className="font-mono">+</span>
              <button type="button" onClick={() => onOpenMaterial?.(m.id)} className="bg-transparent border-0 p-0 cursor-pointer text-inherit hover:underline text-left">{m.name}</button>
              <span className="text-page-muted">({m.kind}, {Number(m.chars || 0).toLocaleString("en-US")} chars)</span>
            </div>
          ))}
          {mats.changed.map((m) => (
            <div key={`mc${m.id}`} className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
              <FileText size={11} /> <span className="font-mono">~</span>
              <button type="button" onClick={() => onOpenMaterial?.(m.id)} className="bg-transparent border-0 p-0 cursor-pointer text-inherit hover:underline text-left">{m.name}</button>
            </div>
          ))}
          {mats.removed.map((m) => (
            <div key={`mr${m.id}`} className="flex items-center gap-1.5 text-red-800 dark:text-red-300"><FileText size={11} /> <span className="font-mono">−</span> {m.name}</div>
          ))}
        </div>
      ) : null}

      {diff ? (
        <div className="space-y-1 text-xs">
          {[["added", diff.added, "+", "text-emerald-800 dark:text-emerald-300"],
            ["changed", diff.changed, "~", "text-amber-800 dark:text-amber-300"],
            ["removed", diff.removed, "−", "text-red-800 dark:text-red-300"]].map(([group, rows, sign, tone]) => rows.length ? (
            <div key={group} className="space-y-1" data-testid={`features-compare-${group}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-page-muted pt-1">{group} · {rows.length}</div>
              {rows.map((f) => {
                const k = `${group}:${f.key}`;
                const isOpen = open.has(k);
                return (
                  <div key={k} className="rounded-md border border-page-divider" data-testid={`features-compare-row-${f.key}`}>
                    <div className={`flex items-start gap-1.5 px-2 py-1 ${tone}`}>
                      <button type="button" onClick={() => toggle(k)} aria-label={isOpen ? "Collapse" : "Expand"}
                        className="mt-0.5 bg-transparent border-0 p-0 cursor-pointer text-inherit shrink-0">
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      <span className="font-mono shrink-0">{sign}</span>
                      <button type="button" onClick={() => toggle(k)} className="text-left bg-transparent border-0 p-0 cursor-pointer text-inherit font-medium">{f.name}</button>
                      {KIND_CHIP[f.kind] ? <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-card-border/40 text-page-muted">{KIND_CHIP[f.kind]}</span> : null}
                      <span className="shrink-0 text-[10px] text-page-muted inline-flex items-center gap-1">
                        {f.origin === "material" ? <ClipboardList size={10} /> : <LayoutGrid size={10} />}
                        {f.origin === "material" ? (f.material_name || "from a source") : "as-built"}
                      </span>
                      {group === "changed" && (f.fields || []).length ? (
                        <span className="shrink-0 text-[10px] text-page-muted" data-testid={`features-compare-fields-${f.key}`}>
                          changed: {f.fields.map((x) => FIELD_LABEL[x] || x).join(", ")}
                        </span>
                      ) : null}
                      {group !== "removed" && onOpenFeature ? (
                        <button type="button" onClick={() => onOpenFeature(f.key)} className="ml-auto shrink-0 text-[10px] text-page-muted hover:text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline">open</button>
                      ) : null}
                    </div>
                    {isOpen ? (
                      <div className="px-2 pb-2 pl-7 space-y-1.5 text-page-text">
                        {group === "changed" && f.was && f.fields?.includes("description") ? (
                          <div className="grid gap-2 md:grid-cols-2">
                            <div className="rounded-md p-1.5 border border-red-500/30 bg-red-500/5">
                              <div className="text-[10px] uppercase tracking-wide text-page-muted mb-0.5">was</div>
                              <div className="whitespace-pre-wrap">{f.was.description || "—"}</div>
                            </div>
                            <div className="rounded-md p-1.5 border border-emerald-500/30 bg-emerald-500/5">
                              <div className="text-[10px] uppercase tracking-wide text-page-muted mb-0.5">now</div>
                              <div className="whitespace-pre-wrap">{f.description || "—"}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap">{f.description || "—"}</div>
                        )}
                        {group === "changed" && f.was && f.fields?.includes("kind") ? <div className="text-page-muted">kind: {f.was.kind} → {f.kind}</div> : null}
                        {group === "changed" && f.was && f.fields?.includes("name") ? <div className="text-page-muted">name: “{f.was.name}” → “{f.name}”</div> : null}
                        {group === "changed" && f.was && (f.fields?.includes("app") || f.fields?.includes("built_ref")) ? (
                          <div className="text-page-muted">implemented by: {f.was.app_title || f.was.app_view || f.was.built_ref || "nothing"} → {f.app_title || f.app_view || f.built_ref || "nothing"}</div>
                        ) : null}
                        {f.quote ? (
                          <div className="text-page-muted">
                            Asked for in {f.material_id && onOpenMaterial
                              ? <button type="button" onClick={() => onOpenMaterial(f.material_id)} className="bg-transparent border-0 p-0 cursor-pointer text-nav-active hover:underline">{f.material_name || `material #${f.material_id}`}</button>
                              : <b>{f.material_name || `material #${f.material_id}`}</b>}: <i>“{String(f.quote).slice(0, 300)}{f.quote.length > 300 ? "…" : ""}”</i>
                            {f.citations ? <> · +{f.citations} more passage{f.citations === 1 ? "" : "s"}</> : null}
                          </div>
                        ) : null}
                        {f.app_view || f.built_ref ? <div className="text-page-muted">Implemented by {f.app_title || f.app_view || f.built_ref} · {f.status}</div> : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null)}
          {!counts.added && !counts.changed && !counts.removed ? (
            <div className="text-page-muted" data-testid="features-compare-empty">No requirement changed between these versions.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
